import { AssetClassesQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { useStatusData } from '@/hooks/useStatusData';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { CREATE_SETTING_MUTATION, DELETE_SETTING_MUTATION, GET_SETTINGS_QUERY, UPDATE_SETTING_MUTATION } from '../queries';
import { AssetClassTargetsModalPage } from './assetClassTargetsModalPage';
import { GET_ASSET_CLASSES_QUERY } from './queries';

export interface AssetClassWithPrevValues {
  id: string;
  name: string;
  defaultPercentage: string;
  prevPercentage: string;
  percentage: string;
  subAssetClasses: SubAssetClassWithPrevValues[];
}

interface SubAssetClassWithPrevValues {
  id: string;
  name: string;
  defaultPercentage: string;
  prevPercentage: string;
  percentage: string;
}

const structurAssetClassesWithPrevValues = (assetClasses: AssetClassesQuery['assetClasses']): AssetClassWithPrevValues[] => {
  const structuredAssetClassesWithPrevValues = assetClasses.map((assetClass) => {
    const structuredSubAssetClassesWithPrevValues = assetClass.subAssetClasses.map((subAssetClass) => {
      return {
        id: subAssetClass.id,
        name: subAssetClass.name,
        defaultPercentage: `${subAssetClass?.defaultAllocationPercentage.toFixed(2)}%`,
        prevPercentage: `${subAssetClass?.defaultAllocationPercentage.toFixed(2)}%`,
        percentage: `${subAssetClass?.defaultAllocationPercentage.toFixed(2)}%`
      };
    });

    return {
      id: assetClass.id,
      name: assetClass.name,
      defaultPercentage: `${assetClass?.defaultAllocationPercentage.toFixed(2)}%`,
      prevPercentage: `${assetClass?.defaultAllocationPercentage.toFixed(2)}%`,
      percentage: `${assetClass?.defaultAllocationPercentage.toFixed(2)}%`,
      subAssetClasses: structuredSubAssetClassesWithPrevValues
    };
  });

  return structuredAssetClassesWithPrevValues;
};

interface AssetClassDefaultTargetsPageProps {
  closeAssetClassTargetsPage: () => void;
  backToTitle?: string;
}

const assetClassOptions = ['Use Curio default asset class targets', "Set my firm's default asset class targets"];

export const AssetClassDefaultTargetsPage = ({ closeAssetClassTargetsPage, backToTitle }: AssetClassDefaultTargetsPageProps) => {
  const { data: statusData } = useStatusData();

  const [isEditMode, setIsEditMode] = useState(false);
  const [assetClassTargets, setAssetClassTargets] = useState<AssetClassWithPrevValues[]>([]);
  const [assetClassSelectedOption, setAssetClassSelectedOption] = useState(assetClassOptions[0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [createSetting] = useMutation(CREATE_SETTING_MUTATION);
  const [updateSetting] = useMutation(UPDATE_SETTING_MUTATION);
  const [deleteSetting] = useMutation(DELETE_SETTING_MUTATION);

  const { data: settingsData, loading: settingsLoading } = useQuery(GET_SETTINGS_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      type: 'assetClassDefaultTargets'
    },
    onCompleted: async (data) => {
      if (!data.Settings.length) {
        const { data: defaultAssetClassesData } = await client.query({
          query: GET_ASSET_CLASSES_QUERY
        });

        const updatedAssetClasses = structurAssetClassesWithPrevValues(defaultAssetClassesData.assetClasses);
        setAssetClassTargets(updatedAssetClasses);
        setIsEditMode(false);
        setAssetClassSelectedOption(assetClassOptions[0]);
        return;
      }

      const assetClasses: AssetClassesQuery['assetClasses'] = JSON.parse(data.Settings[0].value).assetClasses;

      const updatedAssetClasses = structurAssetClassesWithPrevValues(assetClasses);
      setAssetClassTargets(updatedAssetClasses);
      setIsEditMode(true);
      setAssetClassSelectedOption(assetClassOptions[1]);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const saveAssetClassTargets = (assetClassTargets: AssetClassWithPrevValues[], privatePercentage: string) => {
    if (assetClassSelectedOption === assetClassOptions[0] && !settingsData.Settings.length) {
      closeAssetClassTargetsPage();
      return;
    }

    if (assetClassSelectedOption === assetClassOptions[0] && settingsData.Settings.length) {
      deleteSetting({
        variables: {
          id: settingsData.Settings[0].id
        },
        onCompleted: () => {
          closeAssetClassTargetsPage();
        }
      });
      return;
    }

    const hasSubAssetClasses = assetClassTargets.some((assetClass) => assetClass.subAssetClasses.length > 0);

    const updatedAssetClasses = assetClassTargets.map((assetClass) => {
      if (statusData?.target_sub_asset_classes && hasSubAssetClasses) {
        const updatedSubAssetClasses = assetClass.subAssetClasses?.map((subAssetClass) => {
          return {
            id: subAssetClass.id,
            name: subAssetClass.name,
            defaultAllocationPercentage: parseFloat(subAssetClass.percentage) || 0
          };
        });
        return {
          id: assetClass.id,
          name: assetClass.name,
          defaultAllocationPercentage: parseFloat(assetClass.percentage) || 0,
          subAssetClasses: updatedSubAssetClasses
        };
      }

      return {
        id: assetClass.id,
        name: assetClass.name,
        defaultAllocationPercentage: parseFloat(assetClass.percentage) || 0,
        subAssetClasses: []
      };
    });

    if (isEditMode && settingsData.Settings.length) {
      updateSetting({
        variables: {
          data: {
            id: settingsData.Settings[0].id,
            description: 'Asset class defaults',
            value: JSON.stringify({ assetClasses: updatedAssetClasses, privatePercentage: parseFloat(privatePercentage) || 0 })
          }
        },
        onCompleted: () => {
          closeAssetClassTargetsPage();
        }
      });
      return;
    }

    createSetting({
      variables: {
        data: {
          description: 'Asset class defaults',
          type: 'assetClassDefaultTargets',
          key: 'assetClassDefaultTargets',
          value: JSON.stringify({ assetClasses: updatedAssetClasses, privatePercentage: parseFloat(privatePercentage) || 0 })
        }
      },
      onCompleted: () => {
        closeAssetClassTargetsPage();
      }
    });
  };

  if (settingsLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <AssetClassTargetsModalPage
      assetClassTargets={assetClassTargets}
      targetSubAssetClasses={statusData?.target_sub_asset_classes}
      saveAssetClassTargets={saveAssetClassTargets}
      resetAssetClassTargetsToDefaultValues={saveAssetClassTargets}
      closeAssetClassTargetsPage={closeAssetClassTargetsPage}
      assetClassSelectedOption={assetClassSelectedOption}
      setAssetClassSelectedOption={setAssetClassSelectedOption}
      pageTitle="Asset Class Default Targets"
      mode="settings"
    />
  );
};
